import { type ReactNode, useRef, useState } from 'react'
import { cn } from '@/shared/lib'

interface InlineTextProps {
  value: string
  onSave: (value: string) => void
  ariaLabel: string
  type?: 'text' | 'date' | 'datetime-local'
  /** Показ в read-режиме, когда значение пустое. */
  placeholder?: string
  /** Форматирование значения в read-режиме (по умолчанию — как есть). */
  display?: (value: string) => ReactNode
  validate?: (value: string) => string | null
  saving?: boolean
  readClassName?: string
  inputClassName?: string
}

// «Тихий инлайн»: значение выглядит обычным текстом (типографика наследуется от
// окружения), аффорданс правки — только на hover. Клик → компактный редактор по
// размеру значения (layout шапки не прыгает); Enter/blur — сохранить, Esc — отмена.
export function InlineText({
  value,
  onSave,
  ariaLabel,
  type = 'text',
  placeholder = '—',
  display,
  validate,
  saving,
  readClassName,
  inputClassName,
}: InlineTextProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState<string | null>(null)
  // Esc роняет editing → input размонтируется и стреляет blur; флаг гасит
  // сохранение по этому blur.
  const cancelled = useRef(false)

  function start() {
    setDraft(value)
    setError(null)
    setEditing(true)
  }
  function commit() {
    if (cancelled.current) {
      cancelled.current = false
      return
    }
    const next = type === 'text' ? draft.trim() : draft
    const err = validate?.(next) ?? null
    if (err) {
      setError(err)
      return
    }
    setEditing(false)
    if (next !== value) onSave(next)
  }
  function cancel() {
    cancelled.current = true
    setError(null)
    setEditing(false)
  }

  if (!editing) {
    // Есть значение → доступное имя кнопки берётся из видимого текста (иначе
    // aria-label перебивает имя заголовка). Пусто → описываем через aria-label.
    return (
      <button
        type="button"
        onClick={start}
        title={`${ariaLabel}: изменить`}
        {...(value ? {} : { 'aria-label': `${ariaLabel}: задать` })}
        className={cn(
          '-mx-2 -my-0.5 inline-flex max-w-full rounded-control px-2 py-0.5 text-left align-baseline underline-offset-4 transition-colors hover:bg-bg-elevated hover:underline hover:decoration-dotted hover:decoration-border-strong focus-visible:underline focus-visible:decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent',
          !value && 'text-text-tertiary',
          readClassName,
        )}
      >
        {value ? (display ? display(value) : value) : placeholder}
      </button>
    )
  }

  return (
    <span className="relative inline-flex flex-col align-baseline">
      <input
        aria-label={ariaLabel}
        // Инпут появляется по явному клику — автофокус ожидаем.
        ref={(el) => el?.focus()}
        value={draft}
        // Тип идёт как есть: date/datetime-local дают нативный пикер, а текстом
        // пришлось бы вводить ISO-строку руками. Ширина по содержимому (в
        // символах) — только для текста: редактор не шире значения, и шапка не
        // перестраивается. w-* из inputClassName при необходимости перебьёт.
        type={type}
        {...(type === 'text' ? { size: Math.max(draft.length + 1, 4) } : {})}
        disabled={saving}
        // Наследуем шрифт окружения — редактор совпадает с типографикой значения.
        style={{ font: 'inherit' }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
          if (e.key === 'Escape') cancel()
        }}
        onBlur={commit}
        className={cn(
          '-mx-2 -my-0.5 min-w-0 max-w-full rounded-control bg-bg-sunken px-2 py-0.5 text-text-primary caret-accent outline-none ring-1 ring-border-accent disabled:opacity-50',
          inputClassName,
        )}
      />
      {error && (
        <span className="absolute top-full left-0 mt-0.5 whitespace-nowrap text-xs text-accent">
          {error}
        </span>
      )}
    </span>
  )
}
